import { useCallback, useMemo, useState } from "react";
import ConnectyCube from "connectycube";
import { Media, MediaType } from "connectycube/types";
import { useShallow } from "zustand/react/shallow";
import useCallsStore, { ActiveMedia, CallsStoreMediaState } from "./useCallsStore";
import { CallsLogger } from "../helpers";

enum MediaAction {
  MUTE_AUDIO = "audio-mute",
  UNMUTE_AUDIO = "audio-unmute",
  MUTE_VIDEO = "video-mute",
  UNMUTE_VIDEO = "video-unmute",
}

export interface CallsMediaManager extends CallsStoreMediaState {
  isLocalAudioMuted: boolean;
  isLocalVideoMuted: boolean;
  muteAudio: () => void;
  muteVideo: () => void;
  unmuteAudio: () => void;
  unmuteVideo: () => void;
  getUserMedia: (params?: Media.UserParams) => Promise<MediaStream | void>;
  getDisplayMedia: (params?: Media.DisplayParams) => Promise<MediaStream | void>;
  getMediaDevices: (kind?: MediaDeviceKind) => Promise<MediaDeviceInfo[] | void>;
  switchMediaTracks: (options?: Media.TrackConstraintsOrDeviceIds) => Promise<MediaStream | void>;
  setUserMediaParams: (userParams?: Media.UserParams) => void;
  setDisplayMediaParams: (displayParams?: Media.DisplayParams) => void;
  setActiveMedia: (activeMedia?: ActiveMedia) => void;
}

const useCallsMediaManager = (): CallsMediaManager => {
  const [updateKey, setUpdateKey] = useState<number>(0);
  const [
    activeCall,
    localStream,
    remoteStreams,
    userMediaParams,
    saveUserMediaParams,
    displayMediaParams,
    saveDisplayMediaParams,
    activeMedia,
    saveActiveMedia,
  ] = useCallsStore(
    useShallow((store) => [
      store.activeCall,
      store.localStream,
      store.remoteStreams,
      store.userMediaParams,
      store.saveUserMediaParams,
      store.displayMediaParams,
      store.saveDisplayMediaParams,
      store.activeMedia,
      store.saveActiveMedia,
    ]),
  );

  const isLocalAudioMuted = useMemo(() => {
    const audioTracks = localStream?.getAudioTracks();
    return audioTracks ? audioTracks.length === 0 || audioTracks.every((track) => !track.enabled) : true;
  }, [localStream, updateKey]);

  const isLocalVideoMuted = useMemo(() => {
    const videoTracks = localStream?.getVideoTracks();
    return videoTracks ? videoTracks.length === 0 || videoTracks.every((track) => !track.enabled) : true;
  }, [localStream, updateKey]);

  const muteAudio = () => {
    _toggleLocalStreamTracksMuteState(MediaAction.MUTE_AUDIO);
  };

  const muteVideo = () => {
    _toggleLocalStreamTracksMuteState(MediaAction.MUTE_VIDEO);
  };

  const unmuteAudio = () => {
    _toggleLocalStreamTracksMuteState(MediaAction.UNMUTE_AUDIO);
  };

  const unmuteVideo = () => {
    _toggleLocalStreamTracksMuteState(MediaAction.UNMUTE_VIDEO);
  };

  const getUserMedia = async (params?: Media.UserParams) => {
    try {
      const stream = await activeCall?.getUserMedia(params ?? userMediaParams);

      saveActiveMedia(ActiveMedia.USER);
      return stream;
    } catch (error) {
      CallsLogger.error("getUserMedia", { error });
    }
  };

  const getDisplayMedia = async (params?: Media.DisplayParams) => {
    try {
      const stream = await activeCall?.getDisplayMedia(params ?? displayMediaParams);

      saveActiveMedia(ActiveMedia.DISPLAY);
      return stream;
    } catch (error) {
      CallsLogger.error("getDisplayMedia", { error });
    }
  };

  const getMediaDevices = async (kind?: MediaDeviceKind) => {
    try {
      const mediaDevices = await ConnectyCube.videochat.getMediaDevices(kind);

      return mediaDevices;
    } catch (error) {
      CallsLogger.error("getMediaDevices", { error });
    }
  };

  const switchMediaTracks = async (options?: Media.TrackConstraintsOrDeviceIds) => {
    try {
      const stream = await activeCall?.switchMediaTracks(options);

      return stream;
    } catch (error) {
      CallsLogger.error("switchMediaTracks", { error });
    }
  };

  const setUserMediaParams = async (params?: Media.UserParams) => {
    if (params) {
      saveUserMediaParams(params);
    }
  };

  const setDisplayMediaParams = async (params?: Media.DisplayParams) => {
    if (params) {
      saveDisplayMediaParams(params);
    }
  };

  const setActiveMedia = async (activeMedia?: ActiveMedia) => {
    if (activeMedia) {
      saveActiveMedia(activeMedia);
    }
  };

  const _toggleLocalStreamTracksMuteState = useCallback(
    async (action: MediaAction) => {
      if (activeCall) {
        switch (action) {
          case MediaAction.MUTE_AUDIO:
            activeCall.mute(MediaType.AUDIO);
            break;
          case MediaAction.MUTE_VIDEO:
            activeCall.mute(MediaType.VIDEO);
            break;
          case MediaAction.UNMUTE_AUDIO:
            activeCall.unmute(MediaType.AUDIO);
            break;
          case MediaAction.UNMUTE_VIDEO:
            activeCall.unmute(MediaType.VIDEO);
            break;
        }

        setUpdateKey((prevUpdateKey) => prevUpdateKey + 1);
      }
    },
    [activeCall],
  );

  return {
    isLocalAudioMuted,
    isLocalVideoMuted,
    muteAudio,
    muteVideo,
    unmuteAudio,
    unmuteVideo,
    localStream,
    remoteStreams,
    getUserMedia,
    getDisplayMedia,
    getMediaDevices,
    switchMediaTracks,
    userMediaParams,
    setUserMediaParams,
    displayMediaParams,
    setDisplayMediaParams,
    activeMedia,
    setActiveMedia,
  };
};

export default useCallsMediaManager;
